import { ChevronDown, MoreHorizontal } from "lucide-react";
import { Popover } from "../../../components/popover";

interface Option<T extends string> {
  value: T;
  label: string;
}

interface SimpleDropdownProps<T extends string> {
  value: T;
  options: Option<T>[];
  onChange: (value: T) => void;
  icon?: React.ReactNode;
  className?: string;
}

// Small pill dropdown reused for the period/sort/status filters on this page.
export function SimpleDropdown<T extends string>({ value, options, onChange, icon, className }: SimpleDropdownProps<T>) {
  const currentLabel = options.find((o) => o.value === value)?.label || options[0]?.label;

  return (
    <Popover
      className={className}
      trigger={({ toggle, open }) => (
        <button
          onClick={toggle}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-gray-200 text-sm font-semibold text-[#333D4D] hover:bg-gray-50 whitespace-nowrap"
        >
          {icon}
          {currentLabel}
          <ChevronDown size={14} className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      )}
      menuClassName="bg-white border border-gray-100 rounded-xl shadow-lg py-1 min-w-[180px]"
    >
      {(close) =>
        options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => {
              onChange(opt.value);
              close();
            }}
            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${value === opt.value ? "font-semibold text-black" : "text-[#65799A]"
              }`}
          >
            {opt.label}
          </button>
        ))
      }
    </Popover>
  );
}

// Per-item "···" menu — currently just the Enable/Disable Link toggle shown in the design.
export const LinkItemMenu = ({ isActive, onToggleActive }: { isActive: boolean; onToggleActive: () => void }) => (
  <Popover
    trigger={({ toggle }) => (
      <button
        onClick={toggle}
        className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-50"
        aria-label="Link actions"
      >
        <MoreHorizontal size={18} />
      </button>
    )}
    menuClassName="bg-white border border-gray-100 rounded-xl shadow-lg py-1 min-w-[140px]"
  >
    {(close) => (
      <button
        onClick={() => {
          close();
          onToggleActive();
        }}
        className="w-full text-left px-4 py-2 text-sm text-[#333D4D] hover:bg-gray-50"
      >
        {isActive ? "Disable Link" : "Enable Link"}
      </button>
    )}
  </Popover>
);
